import { IMarketSymbol } from 'src/app/shared/models/market-rate';

/**
 * Bảng tỷ giá & giá vàng ở topbar — phép tính THUẦN trên danh mục `MarketSymbols`.
 *
 * Nguồn là realmarketapi.com (job `MarketRateRefreshJob`, mỗi giờ). Nhà cung cấp chỉ có MỘT cặp
 * chứa VND là `USDVND`; mọi ngoại tệ khác quy chéo qua USD, cùng luật với
 * `MarketRateCalculator` ở backend:
 *   - cặp `XXXUSD` (EUR, GBP, AUD, NZD): 1 XXX = XXXUSD × USDVND
 *   - cặp `USDXXX` (JPY, CAD, HKD, SGD, MXN): 1 XXX = USDVND ÷ USDXXX
 *   - cặp không có chân USD (GBPJPY) bị bỏ qua.
 *
 * Bid/ask quy chéo lấy phía BẤT LỢI của cả hai chân (mua thấp nhất, bán cao nhất). Đây là giá
 * THỊ TRƯỜNG giữa các ngân hàng, không phải bảng giá mua tiền mặt / chuyển khoản của một ngân
 * hàng cụ thể — nên bảng không có cột "Mua TM" như bản thiết kế.
 */

export const FX_BASE_CURRENCY = 'VND';

/** 1 lượng = 37,5 g; 1 troy ounce = 31,1034768 g. */
export const OUNCES_PER_LUONG = 37.5 / 31.1034768;

/** Kim loại quý đọc được từ nhà cung cấp, theo thứ tự hiển thị. */
export const FX_METAL_SYMBOLS = ['XAUUSD', 'XAGUSD', 'XPTUSD'] as const;

/** Thứ tự hiển thị ngoại tệ; mã ngoài danh sách xếp sau theo chữ cái. */
const FX_DISPLAY_ORDER = ['USD', 'EUR', 'JPY', 'GBP', 'AUD', 'SGD', 'CAD', 'HKD', 'NZD', 'MXN'];

const FOREX_CLASS = 'forex';

export interface IFxQuote {
  bid: number;
  ask: number;
  mid: number;
}

/** Giá 1 đơn vị ngoại tệ tính bằng VND. */
export interface IFxRateRow extends IFxQuote {
  code: string;
}

export interface IFxMetalRow {
  symbolCode: string;
  /** Giá thế giới, USD / troy ounce. */
  usdPerOunce: IFxQuote;
  /** Quy đổi theo USDVND giữa; `null` khi chưa có giá USDVND. */
  vndPerLuong: number | null;
}

export interface IFxBoard {
  rates: IFxRateRow[];
  metals: IFxMetalRow[];
  /** Mốc giá MỚI NHẤT trong các mã đã dùng (ISO, UTC); `null` = không có giá nào. */
  quotedAt: string | null;
}

/**
 * Chỉ mã ĐANG BẬT và ĐÃ CÓ giá. Mã đang tắt có thể còn giá cũ từ tuần trước — hiển thị nó
 * là hiển thị một con số không ai làm mới nữa. Giá 0 là "chưa lấy giá", không phải giá.
 */
function quoteOf(symbol: IMarketSymbol): IFxQuote | null {
  if (!symbol.isActive || !symbol.lastQuotedAt) return null;
  if (!(symbol.lastBid > 0) || !(symbol.lastAsk > 0)) return null;
  return { bid: symbol.lastBid, ask: symbol.lastAsk, mid: (symbol.lastBid + symbol.lastAsk) / 2 };
}

function orderOf(code: string): number {
  const i = FX_DISPLAY_ORDER.indexOf(code);
  return i < 0 ? FX_DISPLAY_ORDER.length : i;
}

export function buildFxBoard(symbols: IMarketSymbol[]): IFxBoard {
  const forex = new Map<string, IFxQuote>();
  const metalQuotes = new Map<string, IFxQuote>();
  const quotedAtOf = new Map<string, string>();

  for (const symbol of symbols) {
    const quote = quoteOf(symbol);
    if (!quote) continue;
    const code = symbol.symbolCode.trim().toUpperCase();
    if ((FX_METAL_SYMBOLS as readonly string[]).includes(code)) {
      metalQuotes.set(code, quote);
    } else if (symbol.marketClass?.toLowerCase() === FOREX_CLASS && code.length === 6) {
      forex.set(code, quote);
    } else {
      continue;
    }
    quotedAtOf.set(code, symbol.lastQuotedAt as string);
  }

  const used: string[] = [];
  const usdVnd = forex.get('USDVND') ?? null;
  const rates: IFxRateRow[] = [];

  if (usdVnd) {
    used.push('USDVND');
    rates.push({ code: 'USD', ...usdVnd });
    for (const [code, q] of forex) {
      const base = code.slice(0, 3);
      const quote = code.slice(3);
      let row: IFxRateRow | null = null;
      if (quote === 'USD' && base !== FX_BASE_CURRENCY) {
        row = { code: base, bid: q.bid * usdVnd.bid, ask: q.ask * usdVnd.ask, mid: q.mid * usdVnd.mid };
      } else if (base === 'USD' && quote !== FX_BASE_CURRENCY) {
        row = { code: quote, bid: usdVnd.bid / q.ask, ask: usdVnd.ask / q.bid, mid: usdVnd.mid / q.mid };
      }
      if (!row || rates.some((r) => r.code === row.code)) continue;
      rates.push(row);
      used.push(code);
    }
  }
  rates.sort((a, b) => orderOf(a.code) - orderOf(b.code) || a.code.localeCompare(b.code));

  const metals: IFxMetalRow[] = [];
  for (const code of FX_METAL_SYMBOLS) {
    const q = metalQuotes.get(code);
    if (!q) continue;
    used.push(code);
    metals.push({
      symbolCode: code,
      usdPerOunce: q,
      vndPerLuong: usdVnd ? q.mid * usdVnd.mid * OUNCES_PER_LUONG : null,
    });
  }

  let quotedAt: string | null = null;
  for (const code of used) {
    const at = quotedAtOf.get(code);
    if (at && (!quotedAt || Date.parse(at) > Date.parse(quotedAt))) quotedAt = at;
  }

  return { rates, metals, quotedAt };
}

/** Giá GIỮA của 1 đơn vị `code` tính bằng VND; `null` khi thị trường không định giá được. */
export function midInVnd(board: IFxBoard, code: string): number | null {
  if (code === FX_BASE_CURRENCY) return 1;
  return board.rates.find((r) => r.code === code)?.mid ?? null;
}

/** 1 `from` = ? `to`, theo giá giữa; `null` khi thiếu một chân. */
export function crossRate(board: IFxBoard, from: string, to: string): number | null {
  const a = midInVnd(board, from);
  const b = midInVnd(board, to);
  if (a == null || b == null || b === 0) return null;
  return a / b;
}

/** Mã chọn được trong ô quy đổi: VND + mọi ngoại tệ có giá. */
export function convertibleCodes(board: IFxBoard): string[] {
  return board.rates.length ? [FX_BASE_CURRENCY, ...board.rates.map((r) => r.code)] : [];
}

/**
 * Số chữ thập phân khi hiển thị: số lớn (VND/USD ≈ 26.000) không cần phần lẻ, số nhỏ
 * (EUR/USD ≈ 1,17) cần đủ chữ số để thấy biến động.
 */
export function fxFractionDigits(value: number): number {
  const abs = Math.abs(value);
  if (abs >= 1000) return 0;
  if (abs >= 100) return 2;
  return 4;
}
